import { useEffect, useRef } from 'react';
import { fhrAt, tocoAt, valueToY } from '../../engine/fetalEngine';
import styles from './FetalStrip.module.css';

/**
 * A single scrolling CTG channel (FHR or toco) rendered on a slow, wall-clock
 * timebase. Both strips read the same Date.now() clock so their gridlines and
 * events stay vertically aligned (decels under contractions).
 *
 * Props:
 *   channel      'fhr' | 'toco'
 *   fetal        store `fetal` params (drives the sampler)
 *   min,max      value range for the y-axis
 *   gridStep     spacing of horizontal gridlines (value units)
 *   windowSec    seconds of tracing visible
 *   color        trace colour
 *   normalBand   [lo,hi] to lightly shade (FHR 110–160), or null
 */
export default function FetalStrip({ channel, fetal, min, max, gridStep, windowSec, color, normalBand }) {
  const canvasRef = useRef(null);
  const cssDimsRef = useRef({ width: 0, height: 0 });
  const rafRef = useRef(null);
  const fetalRef = useRef(fetal);
  fetalRef.current = fetal;

  // DPR-aware sizing (matches WaveformCanvas approach).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      cssDimsRef.current = { width: rect.width, height: rect.height };
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    const obs = new ResizeObserver(resize);
    obs.observe(canvas);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const PAD = 4;

    const draw = () => {
      const { width: W, height: H } = cssDimsRef.current;
      if (W === 0 || H === 0) { rafRef.current = requestAnimationFrame(draw); return; }
      const f = fetalRef.current;
      const sample = channel === 'fhr' ? (t) => fhrAt(t, f) : (t) => tocoAt(t, f);

      // Background
      ctx.fillStyle = '#100a0e';
      ctx.fillRect(0, 0, W, H);

      // Normal-band shading (FHR 110–160)
      if (normalBand) {
        const y1 = valueToY(normalBand[1], min, max, H, PAD);
        const y2 = valueToY(normalBand[0], min, max, H, PAD);
        ctx.fillStyle = 'rgba(255, 120, 160, 0.06)';
        ctx.fillRect(0, y1, W, y2 - y1);
      }

      // Horizontal value gridlines + labels
      ctx.textBaseline = 'middle';
      ctx.font = '500 10px "Barlow", system-ui, sans-serif';
      for (let v = min; v <= max; v += gridStep) {
        const y = valueToY(v, min, max, H, PAD);
        ctx.strokeStyle = 'rgba(255, 130, 160, 0.16)';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
        ctx.fillStyle = 'rgba(255, 180, 200, 0.5)';
        ctx.fillText(String(v), 3, y);
      }

      // Vertical time gridlines (every 60 s), scrolling with wall clock
      const tNow = Date.now() / 1000;
      const t0 = tNow - windowSec;
      const firstMin = Math.ceil(t0 / 60) * 60;
      for (let m = firstMin; m <= tNow; m += 60) {
        const x = ((m - t0) / windowSec) * W;
        ctx.strokeStyle = 'rgba(255, 130, 160, 0.16)';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
        ctx.stroke();
      }

      // Trace — sampled on a FIXED absolute-time grid (T = k·dt), NOT at fixed
      // screen-x. Each sample is "ink on the paper": drawn once for its timestamp
      // and thereafter it only translates left as the paper scrolls, so already-
      // written values never change. (Sampling at fixed x would re-sample the
      // signal at a slightly different timestamp every frame, making the whole
      // trace — even old data — shimmer in place, which a real CTG never does.)
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.8;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      const dt = Math.max(0.25, windowSec / 1000); // ≤ ~1000 samples across the window
      const kStart = Math.ceil(t0 / dt);
      const kEnd = Math.floor(tNow / dt);
      let started = false;
      for (let k = kStart; k <= kEnd; k++) {
        const T = k * dt;
        const x = ((T - t0) / windowSec) * W;
        const y = valueToY(sample(T), min, max, H, PAD);
        if (!started) { ctx.moveTo(x, y); started = true; } else { ctx.lineTo(x, y); }
      }
      ctx.stroke();

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [channel, min, max, gridStep, windowSec, color, normalBand]);

  return <canvas ref={canvasRef} className={styles.canvas} />;
}
